import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Comprehensive SEO metadata for each page
const SEO_CONFIG = {
  '/': {
    title: 'Minnesota Storm Damage Help | Free Contractor Connection Service',
    description: "Minnesota's #1 storm damage resource for homeowners. Get connected with licensed, vetted roofing contractors. Free service, no obligation. Hail damage, wind damage, insurance claims.",
    keywords: 'Minnesota storm damage, hail damage Minnesota, wind damage roof, roofing contractors Minnesota, storm damage insurance claim, free roof inspection Minnesota',
    schema: 'homepage'
  },
  '/service-areas': {
    title: 'Minnesota Service Areas | Storm Damage Help in 58+ Cities',
    description: 'We help homeowners across all of Minnesota with storm damage. Find trusted roofing contractors in Minneapolis, St. Paul, Bloomington, Eagan, and 54+ more communities.',
    keywords: 'Minnesota roofing contractors, Twin Cities storm damage, Minneapolis hail damage, St Paul roofing, South Metro roofing, Southwest Metro contractors',
    schema: 'serviceAreas'
  },
  '/blog': {
    title: 'Storm Damage & Roofing Blog | Expert Tips for Minnesota Homeowners',
    description: 'Expert advice on storm damage, roofing repairs, insurance claims, and contractor selection. Minnesota-focused insights to protect your home investment.',
    keywords: 'roofing blog, storm damage tips, Minnesota roofing advice, hail damage guide, insurance claim help, roofing contractor tips',
    schema: 'blog'
  },
  '/faqs': {
    title: 'Storm Damage FAQs | Minnesota Homeowner Questions Answered',
    description: 'Get answers to 50+ common questions about storm damage, roof repairs, insurance claims, contractor selection, and costs in Minnesota.',
    keywords: 'storm damage FAQ, roofing questions Minnesota, insurance claim help, roof repair cost, hail damage questions, wind damage FAQ',
    schema: 'faq'
  },
  '/insurance-agents': {
    title: 'Find a Minnesota Insurance Agent | Homeowner Insurance Referrals',
    description: 'Looking for a new homeowners insurance agent in Minnesota? Get referrals to trusted local agents who understand storm damage coverage.',
    keywords: 'Minnesota insurance agent, homeowners insurance Minnesota, insurance agent referral, storm damage coverage, hail damage insurance',
    schema: 'insuranceAgents'
  },
  '/resources': {
    title: 'Storm Damage Resources | Minnesota Homeowner Education Center',
    description: 'Educational resources for Minnesota homeowners dealing with storm damage. Statistics, guides, and expert information to help you make informed decisions.',
    keywords: 'storm damage resources, Minnesota storm statistics, roofing education, homeowner guides, insurance claim resources',
    schema: 'resources'
  }
};

// City-specific SEO with enhanced local signals
const getCitySEO = (cityName, citySlug, region) => ({
  title: `${cityName} Storm Damage Help | Trusted Roofers in ${cityName}, MN`,
  description: `Storm damage in ${cityName}, Minnesota? Connect with licensed, insured roofing contractors serving ${cityName} and ${region}. Free assessment, no obligation. Hail & wind damage experts.`,
  keywords: `${cityName} storm damage, ${cityName} roofing contractors, ${cityName} hail damage, ${cityName} MN roofers, ${cityName} roof repair, ${region} contractors`,
  schema: 'cityPage'
});

// Blog post SEO
const getBlogSEO = (post) => ({
  title: `${post.title} | Minnesota Storm Damage Blog`,
  description: post.excerpt,
  keywords: `${post.category}, Minnesota roofing, storm damage tips, ${post.title.toLowerCase().split(' ').slice(0, 5).join(', ')}`,
  schema: 'blogPost'
});

// Inject JSON-LD Schema
const injectSchema = (schemaType, data = {}) => {
  // Remove existing schema
  const existingSchemas = document.querySelectorAll('script[data-schema-type]');
  existingSchemas.forEach(el => el.remove());

  let schema = null;

  switch (schemaType) {
    case 'homepage':
      schema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Minnesota Storm Damage Resource Center",
        "url": "https://stormcaremn.com",
        "description": "Minnesota's trusted resource for storm damage guidance and contractor connections",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://stormcaremn.com/service-areas?search={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      };
      break;

    case 'serviceAreas':
      schema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Minnesota Storm Damage Service Areas",
        "description": "Cities and regions served by Minnesota Storm Damage Resource Center",
        "numberOfItems": 58,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Minneapolis", "url": "https://stormcaremn.com/service-areas/minneapolis" },
          { "@type": "ListItem", "position": 2, "name": "St. Paul", "url": "https://stormcaremn.com/service-areas/st-paul" },
          { "@type": "ListItem", "position": 3, "name": "Bloomington", "url": "https://stormcaremn.com/service-areas/bloomington" },
          { "@type": "ListItem", "position": 4, "name": "Eden Prairie", "url": "https://stormcaremn.com/service-areas/eden-prairie" },
          { "@type": "ListItem", "position": 5, "name": "Minnetonka", "url": "https://stormcaremn.com/service-areas/minnetonka" }
        ]
      };
      break;

    case 'cityPage':
      schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": `Storm Damage Help - ${data.cityName}, Minnesota`,
        "description": `Professional storm damage contractor connections for ${data.cityName}, MN homeowners`,
        "url": `https://stormcaremn.com/service-areas/${data.citySlug}`,
        "areaServed": {
          "@type": "City",
          "name": data.cityName,
          "containedInPlace": {
            "@type": "State",
            "name": "Minnesota"
          }
        },
        "serviceType": ["Storm Damage Assessment", "Roofing Contractor Connection", "Insurance Claim Guidance"],
        "priceRange": "Free"
      };
      break;

    case 'blog':
      schema = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Minnesota Storm Damage & Roofing Blog",
        "description": "Expert insights on storm damage, roofing, and insurance for Minnesota homeowners",
        "url": "https://stormcaremn.com/blog",
        "publisher": {
          "@type": "Organization",
          "name": "Minnesota Storm Damage Resource Center"
        }
      };
      break;

    case 'blogPost':
      schema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": data.title,
        "description": data.excerpt,
        "datePublished": data.date,
        "author": {
          "@type": "Organization",
          "name": "Minnesota Storm Damage Resource Center"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Minnesota Storm Damage Resource Center",
          "url": "https://stormcaremn.com"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://stormcaremn.com/blog/${data.slug}`
        },
        "articleSection": data.category,
        "keywords": data.keywords
      };
      break;

    case 'faq':
      schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How do I know if my roof has storm damage?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Look for missing or damaged shingles, granules in gutters, dented vents or flashing, and water stains inside your home. After a storm, it's best to have a professional inspection since much damage isn't visible from the ground."
            }
          },
          {
            "@type": "Question",
            "name": "How much does roof repair cost in Minnesota?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Minor repairs typically cost $150-$400, while major repairs range from $1,000-$3,000. Full replacement averages $8,000-$15,000 depending on size and materials. Insurance may cover storm damage costs."
            }
          },
          {
            "@type": "Question",
            "name": "Should I file an insurance claim for roof damage?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "File a claim if damage exceeds your deductible and the damage was caused by a covered event like hail or wind. Document everything with photos and get a professional assessment before deciding."
            }
          },
          {
            "@type": "Question",
            "name": "How do I choose a reliable roofing contractor?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Verify Minnesota licensing, check insurance coverage, read reviews, get multiple written estimates, and avoid contractors who demand large upfront payments or pressure you to sign immediately."
            }
          },
          {
            "@type": "Question",
            "name": "What is the difference between roof repair and replacement?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Repair fixes specific damaged areas and is best when damage is localized and the roof is relatively new. Replacement involves removing all old materials and installing new, recommended when damage is extensive or the roof is aging."
            }
          }
        ]
      };
      break;

    case 'insuranceAgents':
      schema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Insurance Agent Referral Service",
        "serviceType": "Insurance Agent Connection",
        "provider": {
          "@type": "Organization",
          "name": "Minnesota Storm Damage Resource Center"
        },
        "areaServed": {
          "@type": "State",
          "name": "Minnesota"
        },
        "description": "Free referrals to trusted Minnesota insurance agents specializing in homeowners coverage"
      };
      break;

    default:
      return;
  }

  if (schema) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema-type', schemaType);
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  }
};

// Breadcrumb Schema Generator
const injectBreadcrumbs = (breadcrumbs) => {
  const existingBreadcrumbs = document.querySelector('script[data-breadcrumbs]');
  if (existingBreadcrumbs) existingBreadcrumbs.remove();

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://stormcaremn.com${item.path}`
    }))
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-breadcrumbs', 'true');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};

export const useSEO = (cityData = null, blogData = null) => {
  const location = useLocation();
  
  useEffect(() => {
    let seo;
    let breadcrumbs = [{ name: 'Home', path: '/' }];
    let canonicalPath = location.pathname; // Default canonical is the pathname without query params
    
    // Determine SEO config based on route
    if (location.pathname.startsWith('/service-areas/') && cityData) {
      seo = getCitySEO(cityData.name, cityData.slug, cityData.region || 'the Twin Cities Metro');
      injectSchema('cityPage', { cityName: cityData.name, citySlug: cityData.slug });
      breadcrumbs.push({ name: 'Service Areas', path: '/service-areas' });
      breadcrumbs.push({ name: cityData.name, path: `/service-areas/${cityData.slug}` });
      // Self-referencing canonical for city pages
      canonicalPath = `/service-areas/${cityData.slug}`;
    } else if (location.pathname.startsWith('/blog/') && blogData) {
      seo = getBlogSEO(blogData);
      injectSchema('blogPost', blogData);
      breadcrumbs.push({ name: 'Blog', path: '/blog' });
      breadcrumbs.push({ name: blogData.title.substring(0, 30) + '...', path: `/blog/${blogData.slug}` });
      // Self-referencing canonical for blog posts
      canonicalPath = `/blog/${blogData.slug}`;
    } else if (location.pathname === '/blog') {
      // Blog listing page - canonical should always be /blog without query params
      seo = SEO_CONFIG['/blog'];
      injectSchema('blog');
      breadcrumbs.push({ name: 'Blog', path: '/blog' });
      canonicalPath = '/blog'; // Strip any query parameters
    } else {
      seo = SEO_CONFIG[location.pathname] || SEO_CONFIG['/'];
      if (seo.schema) {
        injectSchema(seo.schema);
      }
      
      // Set breadcrumbs for main pages
      if (location.pathname !== '/') {
        const pageName = location.pathname.split('/').pop().replace(/-/g, ' ');
        breadcrumbs.push({ 
          name: pageName.charAt(0).toUpperCase() + pageName.slice(1), 
          path: location.pathname 
        });
      }
    }
    
    // Inject breadcrumbs
    injectBreadcrumbs(breadcrumbs);
    
    // Update document title
    document.title = seo.title;
    
    // Update or create meta tags
    const updateMeta = (selector, attribute, content) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attribute, content);
      } else {
        el = document.createElement('meta');
        const [type, name] = selector.replace(/[[\]']+/g, '').split('=');
        el.setAttribute(type.replace('meta', '').replace('[', ''), name);
        el.setAttribute(attribute, content);
        document.head.appendChild(el);
      }
    };
    
    // Primary meta tags
    updateMeta('meta[name="description"]', 'content', seo.description);
    updateMeta('meta[name="keywords"]', 'content', seo.keywords);
    
    // Open Graph tags
    updateMeta('meta[property="og:title"]', 'content', seo.title);
    updateMeta('meta[property="og:description"]', 'content', seo.description);
    updateMeta('meta[property="og:url"]', 'content', `https://stormcaremn.com${canonicalPath}`);
    updateMeta('meta[property="og:type"]', 'content', location.pathname.startsWith('/blog/') ? 'article' : 'website');
    
    // Twitter tags
    updateMeta('meta[property="twitter:title"]', 'content', seo.title);
    updateMeta('meta[property="twitter:description"]', 'content', seo.description);
    updateMeta('meta[property="twitter:url"]', 'content', `https://stormcaremn.com${canonicalPath}`);
    
    // Canonical URL - always use non-www and strip query parameters
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://stormcaremn.com${canonicalPath}`);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `https://stormcaremn.com${canonicalPath}`);
      document.head.appendChild(canonical);
    }
    
  }, [location.pathname, cityData, blogData]);
};

export default useSEO;
